import './styles.scss';
import React, {Dispatch, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";

/* Types, Constants, Utils */
import Utils from "../../../utils";
import {RecordsContext} from "../../../types/document";
import * as Constants from "../../../constants";

/* Bootstrap */
import {Card, Col, Container, Row} from "react-bootstrap";

/* Redux */
import {AnyAction} from "@reduxjs/toolkit";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../redux";
import {IResultsState, ISelected} from "../../../types/redux/result";
import {ISearchState} from "../../../types/redux/search";
import {
  addToSelectedResults,
  removeFromSelectedResults,
  setInitialSelection,
  setSelectedResults
} from "../../../redux/slices/result-slice";
import {IUserState} from "../../../types/redux/user";

/* Hooks */
import {useSearchStateFromParams} from "../../../hooks";

/* Components */
import MediaPreview from "../media-preview";
import Tags from "../../tags-notes/tags/tags";
import CardHeader from '../card-header';
import TagsModal from "../../tags-notes/tags/tag-modal";
import Note from "../../tags-notes/notes";
import {ISavedState, TSavedDocument} from "../../../types/redux/saved";
import useCardField, {IUseCardField} from "../hooks/useCardField";
import {NavigateFunction} from "react-router/dist/lib/hooks";
import LinkButton from "../../button-link";
import MetaFields from "../components/meta-fields";

interface IProps  {
  record: any;
  index: number;
}

const DocumentCard = (props: IProps):JSX.Element => {
  const {
    record,
    index
  } = props;

  /* redux */
  const searchState: ISearchState = useSelector<RootState, ISearchState>(state => state.search);
  const savedState: ISavedState = useSelector<RootState, ISavedState>(state => state.saved);
  const resultsState: IResultsState = useSelector<RootState, IResultsState>(state => state.result);
  const userState: IUserState = useSelector<RootState, IUserState>(state => state.user);
  const dispatch:Dispatch<AnyAction> = useDispatch();
  const navigate: NavigateFunction = useNavigate();

  /* state */
  const [expanded, setExpanded] = useState<boolean>(resultsState.expand_all);
  const [selected, setSelected] = useState<boolean>(false);
  const [savedDocumentData, setSavedDocumentData] = useState<TSavedDocument | null>(null);

  /* hooks */
  const {currentSearchParams} = useSearchStateFromParams();
  const {getFieldContent}: IUseCardField = useCardField();

  useEffect(()=>{
    /* toolbar expand all */
    setExpanded(resultsState.expand_all);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[resultsState.expand_all, searchState.query])

  useEffect(()=>{
    if (!record) return;

    /* toolbar select all */
    setSelected(resultsState.selected_ids.findIndex((select: ISelected) => select.id === record.id) !== -1);
  }, [resultsState.selected_ids, record?.id, record])

  useEffect(()=>{
    if (!record) return;

    /* saved documents */
    const savedDocument: TSavedDocument | undefined = savedState.documents.find((document: TSavedDocument) => document.documentId === record.id);
    setSavedDocumentData(savedDocument ? savedDocument : null)
  }, [record, savedState.documents])

  const handleLinkClick = (): void =>{
    dispatch(setInitialSelection(record.id));
    dispatch(setSelectedResults([record.id]))
  }

  const getLabel = (): JSX.Element => {
    let title: string;
    if (record.highlighting && record.highlighting.ti) {
      title = record.highlighting.ti;
    } else {
      title = record.ti ? record.ti : 'Unknown Title';
    }

    const link: string = '/' + Utils.convertToKeyOrId(Array.isArray(record.industry) ? record.industry[0] : record.industry) + '/documents/' + Constants.VIEWER + '/?' + Constants.INITIAL_ID + '=' + record.id + '&' + Constants.ID + '=' + record.id + '&' + currentSearchParams().toString()
    const resultNumber: number = (searchState.current_page - 1) * searchState.results_per_page + index + 1;
    return (
      <div className={'d-flex flex-row mt-2'}>
        <span className={'me-2 text-link-color'}>{resultNumber}. </span>
        <div
          className={"header-link text-large text-link-color"}
          onClick={()=>handleLinkClick()}
        >
          {getFieldContent('title', title, 'Title', link).elem}
        </div>
      </div>
    )
  }

  const handleSelected = (selected: boolean): void => {
    setSelected(selected);
    if (selected) {
      dispatch(addToSelectedResults([record.id]))
    } else {
      dispatch(removeFromSelectedResults([record.id]))
    }
  }

  const handleViewDocumentClick = (): void => {
    dispatch(setInitialSelection(record.id));
    dispatch(setSelectedResults([record.id]));
    const url: string = '/' + Utils.convertToKeyOrId(record.industry) + '/documents/' + Constants.VIEWER + '/?' + Constants.INITIAL_ID + '=' + record.id + '&' + Constants.ID + '=' + record.id + '&' + currentSearchParams().toString();
    navigate(url);
  }

  return (
    <RecordsContext.Provider value={{records:[record]}}>
      {record && <Card className={"document-card m-0 mb-3 p-0"}>
        <CardHeader
          selected={selected}
          handleSelected={handleSelected}
          getLabel={getLabel}
        />

        <Card.Body>
          <Container className={'px-2 pt-2 text-normal '}>
            {(resultsState.results.length > 0) &&
              <Row className={'mb-0'}>
                <Col sm={10} >
                  <MetaFields
                    record={record}
                    setColumns={true}
                    noFold={false}
                    expandAll={expanded}
                  />

                  {/* Tags  */}
                  <Row className={'doc-tags mb-1'}>
                    <Col sm={2}>
                      <span className={'me-4'}><b>Tags:</b></span>
                    </Col>

                    <Col sm={10} className={'tags-applied ps-2'}>
                      <Tags appliedTags={savedDocumentData?.tags}/>
                      <TagsModal appliedTags={savedDocumentData?.tags}/>
                    </Col>
                  </Row>

                  {/* Note */}
                  {userState.loggedIn &&
                    <Row className={'doc-note doc-content mt-3 mx-0 px-0'}>
                      <Col className={'px-0 text-nowrap'}>
                        <span className={'me-3 '}><b>Notes:</b></span>
                        <Note context={Constants.DOCUMENT}/>
                      </Col>
                    </Row>}
                </Col>

                {/* Preview Image */}
                <Col lg={2} className={'d-none d-lg-block h-100 center'}>
                  <MediaPreview context={Constants.SEARCH}/>
                </Col>

              </Row>}

            <Row className={'doc-view mt-0'}>
              <Col xs={12} className={'text-end'}>
                <LinkButton
                  label={'View Document'}
                  clickFunc={handleViewDocumentClick}
                  linkArrow={true}
                />
              </Col>
            </Row>
          </Container>
        </Card.Body>
      </Card>}
    </RecordsContext.Provider>
  )
}

export default DocumentCard;
